import { useSnackbar } from 'notistack';
import AppLayout from '../layout/AppLayout';
import { HeaderProjects as Header } from '../layout/Header';
import { CheckoutProvider, PaymentElement, useCheckout, AddressElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { authenticatedFetch } from '../authentication/authenticatedFetch';
import Button from '../ui/Button';
import styled from '@emotion/styled';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import LoadingButton from '../ui/LoadingButton';
import { useTranslation } from 'react-i18next';
import { getUser } from '../authentication/context/useUser';
import { useQuery } from 'react-query';
import useConfiguration from '../useConfiguration';
import Markdown from '../ui/Markdown';

const CheckoutContainer = styled.div`
  display: flex;
  gap: 2rem;
  padding: 2rem;
`;

const InvoiceTable = styled.div`
  flex: 1;
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
`;

const TableRow = styled.tr`
  &:last-child {
    border-top: 2px solid #eee;
    font-weight: bold;
  }
`;

const TableCell = styled.td`
  padding: 1rem 1rem 1rem 0;
  white-space: nowrap;
  text-align: ${props => props.align || 'left'};
`;

const PaymentForm = styled.form`
  flex: 2;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 2rem;
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const PromoCodeForm = styled.form`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const PromoCodeInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  flex: 1;
`;

const PromoCodeButton = styled(Button)`
  padding: 0.5rem 1rem;
`;


const Checkout = () => {
  const checkout = useCheckout();
  const { enqueueSnackbar } = useSnackbar();
  const [promoCode, setPromoCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const { data: user } = useQuery('user', getUser, {
    useErrorBoundary: true,
  });
  const [country, setCountry] = useState('');
  const { configuration } = useConfiguration();
  const allowedCountries = configuration['app.allowedCountries']?.split(',') || [];

  useEffect(() => {
    if (user) {
      if (
        user.subscriptionStatus === 'success' &&
        user.subscriptionExpirationAt &&
        new Date(user.subscriptionExpirationAt) > new Date()
      ) {
        navigate('/checkout/status');
      } else if (user.subscriptionStatus === 'processing') {
        navigate('/checkout/status');
      }
    }
  }, [user, navigate]);

  const handlePromoCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await checkout.applyPromotionCode(promoCode);
    setPromoCode('');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!allowedCountries.includes(country)) {
      enqueueSnackbar(
        <Markdown>
          {t('common:subscription.checkoutPage.form.countryNotAllowed')}
        </Markdown>, { variant: 'error' });
      return;
    }
    try {
      setLoading(true);
      const result = await checkout.confirm();
      setLoading(false);
      if (result.type === 'error') {
        enqueueSnackbar(result.error.message, { variant: 'error' });
      } else {
        enqueueSnackbar(t('common:subscription.checkoutPage.form.paymentSuccessful'), { variant: 'success' });
      }
    } catch (error) {
      setLoading(false);
      enqueueSnackbar('An error occurred during checkout', { variant: 'error' });
    }
  };

  const hasTax = checkout.taxAmounts?.some(tax => tax.minorUnitsAmount > 0);

  return (
    <AppLayout header={<Header />}>
      <CheckoutContainer>
        <InvoiceTable>
          <h2>{t('common:subscription.checkoutPage.form.subscriptionDetails')}</h2>
          <Table>
            <tbody>
              {checkout.lineItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="right">{item.subtotal.minorUnitsAmount / 100}€</TableCell>
                </TableRow>
              ))}
              {checkout.taxAmounts?.filter(tax => tax.minorUnitsAmount > 0).map((tax, index) => (
                <TableRow key={index}>
                  <TableCell>{tax.displayName}</TableCell>
                  <TableCell align="right">
                    {tax.minorUnitsAmount / 100}€
                    {tax.inclusive ? t('common:subscription.checkoutPage.form.vat.inclusive') : t('common:subscription.checkoutPage.form.vat.exclusive')}
                  </TableCell>
                </TableRow>
              ))}
              {checkout.discountAmounts?.map((discount, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {t('common:subscription.checkoutPage.form.promoCode.placeholder')} : {discount.displayName}</TableCell>
                  <TableCell align="right">-{discount.minorUnitsAmount / 100}€</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell>{t('common:subscription.checkoutPage.form.total')}</TableCell>
                <TableCell align="right">{checkout.total.total.minorUnitsAmount / 100}€</TableCell>
              </TableRow>
            </tbody>
          </Table>
          <PromoCodeForm onSubmit={handlePromoCodeSubmit}>
            <PromoCodeInput
              type="text"
              placeholder={t('common:subscription.checkoutPage.form.promoCode.placeholder')}
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
            />
            <PromoCodeButton type="submit">
              {t('common:subscription.checkoutPage.form.promoCode.button')}
            </PromoCodeButton>
          </PromoCodeForm>
        </InvoiceTable>
        <PaymentForm onSubmit={handleSubmit}>
          <h1>{t('common:subscription.checkoutPage.form.paymentInfo')}</h1>
          <AddressElement
            options={{
              mode: 'billing',
            }}
            onChange={(event) => {
              setCountry(event.value.address.country);
            }}
          />
          <PaymentElement
            options={{
              layout: {
                type: 'tabs',
                defaultCollapsed: false,
              },
            }}
          />
          <LoadingButton type="submit" loading={loading}>
            {t('common:subscription.checkoutPage.subscriptionButton')}
          </LoadingButton>
        </PaymentForm>
      </CheckoutContainer>
    </AppLayout>
  );
};


const CheckoutWithProvider = () => {
  const { i18n } = useTranslation();

  const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '', {
    locale: i18n.language === 'fr' ? 'fr' : 'en',
  });

  const fetchClientSecret = async () => {
    try {
      const response = await authenticatedFetch(
        `${process.env.REACT_APP_API_URL}/checkout/create-checkout-session`,
        {
          method: 'POST',
          body: JSON.stringify({ language: i18n.language }),
        }
      );

      const { clientSecret } = await response.json();

      return clientSecret;
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <CheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
      <Checkout />
    </CheckoutProvider>
  );
};
export default CheckoutWithProvider;
